import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../utils/i18n';
import './PWAInstallPrompt.css';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const { language } = useAuth();
  const { t } = useTranslation(language);

  useEffect(() => {
    // Check if already dismissed or installed
    const isDismissed = localStorage.getItem('pwa-install-dismissed');
    const isInstalled = window.matchMedia('(display-mode: standalone)').matches;

    if (isDismissed || isInstalled) {
      return;
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Save the event so it can be triggered later
      setDeferredPrompt(e);
      // Show custom install prompt after 3 seconds
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    const handleAppInstalled = () => {
      setShowPrompt(false);
      setDeferredPrompt(null);
      localStorage.setItem('pwa-install-dismissed', 'true');
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }

    // Clear the deferred prompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  if (!showPrompt || !deferredPrompt) {
    return null;
  }

  const getTitle = () => {
    if (language === 'vi') return 'Cài đặt ứng dụng';
    if (language === 'ko') return '앱 설치';
    return 'Install App';
  };

  const getMessage = () => {
    if (language === 'vi') return 'Cài đặt Link to KMO để truy cập nhanh hơn và sử dụng offline';
    if (language === 'ko') return 'Link to KMO를 설치하여 더 빠르게 접속하고 오프라인으로 사용하세요';
    return 'Install Link to KMO for faster access and offline use';
  };

  const getInstallText = () => {
    if (language === 'vi') return 'Cài đặt';
    if (language === 'ko') return '설치';
    return 'Install';
  };

  const getDismissText = () => {
    if (language === 'vi') return 'Để sau';
    if (language === 'ko') return '나중에';
    return 'Later';
  };

  return (
    <div className="pwa-install-prompt">
      <div className="pwa-install-content">
        <div className="pwa-install-icon">
          <img src="/logo.jpg" alt="Link to KMO" />
        </div>
        <div className="pwa-install-text">
          <h3>{getTitle()}</h3>
          <p>{getMessage()}</p>
        </div>
        <div className="pwa-install-actions">
          <button className="pwa-install-dismiss" onClick={handleDismiss}>
            {getDismissText()}
          </button>
          <button className="pwa-install-confirm" onClick={handleInstallClick}>
            {getInstallText()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
